import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getEbookById } from "../../app/api";
import { UserContext } from "../../providers/UserProvider";
import { useTranslation } from 'react-i18next';

const HTMLTextView = ({ htmlContent }: any) => {
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

const EbookDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { userBio } = useContext(UserContext);
  const [ebook, setEbook] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getEbookById(id)
      .then((res) => {
        setEbook(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
    window.scrollTo(0, 0);
  }, [id]);

  if (loading || !ebook) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      {/* Dark hero */}
      <div className="relative overflow-hidden bg-[#0a1628] -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative pt-40 pb-12 sm:pt-48 sm:pb-16">
          <div className="mx-auto max-w-[1120px] px-6 lg:px-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-secondary-300 backdrop-blur-sm">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
              {t('ebook.badge')}
            </div>
            <h1 className="text-[26px] sm:text-[36px] font-extrabold text-white leading-tight">
              {ebook.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-sm">
              <span className="font-semibold text-white">{ebook.author}</span>
              {ebook.category && (
                <>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-400">{ebook.category}</span>
                </>
              )}
              {ebook.pageCount > 0 && (
                <>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-400">{ebook.pageCount} {t('ebook.pages')}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white pb-12">
        <div className="mx-auto max-w-[1120px] px-6 lg:px-10">
          {/* Cover image */}
          <div className="relative -mt-6">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-transparent to-primary-500/20 rounded-2xl blur-2xl" />
            <div className="relative rounded-xl overflow-hidden border border-neutral-200 shadow-xl max-w-[300px]">
              <img src={ebook.cover} alt={ebook.title} className="w-full h-auto object-cover" />
            </div>
          </div>

          {/* Grid layout */}
          <div className="grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-start mt-10">
            {/* Left - description */}
            <div>
              <div className="pb-6 border-b border-neutral-200">
                <h2 className="text-xl font-bold text-neutral-900 mb-4">{t('ebookDetail.aboutBook')}</h2>
                <div className="prose prose-lg max-w-none text-neutral-700 leading-relaxed">
                  <HTMLTextView htmlContent={ebook.description} />
                </div>
              </div>

              <div className="py-6 border-b border-neutral-200 space-y-4">
                <div className="flex gap-4">
                  <svg className="w-6 h-6 text-neutral-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                  <div>
                    <p className="font-semibold text-neutral-900 text-[15px]">{t('ebookDetail.pageCount')}</p>
                    <p className="text-sm text-neutral-500">{ebook.pageCount} {t('ebook.pages')}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <svg className="w-6 h-6 text-neutral-700 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
                  </svg>
                  <div>
                    <p className="font-semibold text-neutral-900 text-[15px]">{t('ebook.language')}</p>
                    <p className="text-sm text-neutral-500">{ebook.language === 'mn' ? t('ebook.languageMn') : t('ebook.languageEn')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - purchase card */}
            <div className="lg:sticky lg:top-24">
              <div className="border border-neutral-200 rounded-xl p-6 shadow-lg">
                <div className="mb-6">
                  <span className="text-[22px] font-extrabold text-neutral-900">
                    ₮{ebook.price?.toLocaleString()}
                  </span>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden mb-4">
                  <div className="p-3">
                    <p className="text-[10px] font-bold text-neutral-900 uppercase tracking-wider">{t('ebook.author')}</p>
                    <p className="text-sm text-neutral-700 mt-0.5">{ebook.author}</p>
                  </div>
                  {ebook.category && (
                    <div className="border-t border-neutral-200 p-3">
                      <p className="text-[10px] font-bold text-neutral-900 uppercase tracking-wider">{t('ebook.category')}</p>
                      <p className="text-sm text-neutral-700 mt-0.5">{ebook.category}</p>
                    </div>
                  )}
                </div>

                {ebook.purchased ? (
                  <Link
                    to={`/ebook/read/${id}`}
                    className="block w-full text-center py-3.5 rounded-lg bg-green-600 text-white font-semibold text-base hover:bg-green-700 transition-colors"
                  >
                    {t('ebook.read')}
                  </Link>
                ) : userBio ? (
                  <Link
                    to={`/ebook/payment/${id}`}
                    className="block w-full text-center py-3.5 rounded-lg bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-400 text-white font-semibold text-base hover:opacity-90 transition-opacity"
                  >
                    {t('ebookDetail.purchase')}
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="block w-full text-center py-3.5 rounded-lg bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-400 text-white font-semibold text-base hover:opacity-90 transition-opacity"
                  >
                    {t('ebookDetail.loginToPurchase')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EbookDetail;
